import { useQueries } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Account } from '@/api/commercial/accounts';
import { accountApi } from '@/api/commercial/accounts';
import { ContractApiError } from '@/api/commercial/contracts';
import {
  type ClientContractFilterValues,
  FILTER_ALL,
} from '@/components/commercial/contracts/client-contracts/client-contract-filters';
import type { ClientContractEntry } from '@/components/commercial/contracts/client-contracts/client-contract-table';
import { ClientContractsTab } from '@/components/commercial/contracts/client-contracts/client-contracts-tab';
import { ContractFormDialog } from '@/components/commercial/contracts/contract-form-dialog';
import {
  fromApiContractType,
  STANDARD_CONTRACT_TYPES,
  type StandardContractTypeId,
  type StandardContractView,
  toApiContractType,
} from '@/components/commercial/contracts/contract-types';
import { ContractHistoryDialog } from '@/components/commercial/contracts/history/contract-history-dialog';
import { StandardContractsTab } from '@/components/commercial/contracts/standard-contracts/standard-contracts-tab';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useActivateContract,
  useContracts,
  useCreateContract,
  useCreateContractVersion,
  useInactivateContract,
  useUpdateContract,
} from '@/hooks/commercial/use-contracts';
import { useToast } from '@/hooks/use-toast';
import { type Contract, ContractScope, type ContractType } from '@/types/commercial/contracts';

type ContractsTab = 'standard' | 'clients';
type EditorState = { scope: ContractScope; contract?: Contract; initialType?: ContractType } | null;

const DEFAULT_TAB: ContractsTab = 'standard';
const CLIENT_PAGE_SIZE = 10;

function resolveTab(value: string | null): ContractsTab {
  return value === 'clients' ? 'clients' : DEFAULT_TAB;
}

function accountName(account?: Account): string {
  return account?.social_name || account?.fantasy_name || account?.name || 'Cliente não encontrado';
}

function errorMessage(error: unknown): string {
  if (error instanceof ContractApiError) return error.message;
  return 'Não foi possível concluir a operação.';
}

function isStandardContractTypeId(value: string): value is StandardContractTypeId {
  return STANDARD_CONTRACT_TYPES.some((type) => type.id === value);
}

export function ContractListPage() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = resolveTab(searchParams.get('tab'));
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<ClientContractFilterValues>({
    client: '',
    type: FILTER_ALL,
    status: FILTER_ALL,
  });
  const [editor, setEditor] = useState<EditorState>(null);
  const [historyTarget, setHistoryTarget] = useState<ClientContractEntry | null>(null);

  const typeFilter = isStandardContractTypeId(filters.type)
    ? toApiContractType(filters.type)
    : undefined;

  const standardQuery = useContracts(
    { scope: ContractScope.STANDARD, page: 1, limit: 100 },
    currentTab === 'standard',
  );
  const clientQuery = useContracts(
    {
      scope: ContractScope.CLIENT,
      page,
      limit: CLIENT_PAGE_SIZE,
      type: typeFilter,
      active: filters.status === FILTER_ALL ? undefined : filters.status === 'active',
    },
    currentTab === 'clients',
  );

  const createContract = useCreateContract();
  const updateContract = useUpdateContract();
  const activateContract = useActivateContract();
  const inactivateContract = useInactivateContract();
  const createVersion = useCreateContractVersion();

  const clientContracts = clientQuery.data?.docs ?? [];
  const accountIds = [...new Set(clientContracts.flatMap((item) => item.accountId ?? []))];
  const accountQueries = useQueries({
    queries: accountIds.map((id) => ({
      queryKey: ['accounts', 'detail', id],
      queryFn: () => accountApi.findOne(id),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const accountsById = new Map(
    accountIds.map((id, index) => [id, accountQueries[index]?.data] as const),
  );

  const standardContracts = useMemo<StandardContractView[]>(() => {
    return STANDARD_CONTRACT_TYPES.map((type) => {
      const matchingContracts = standardQuery.data?.docs.filter(
        (item) => item.type === toApiContractType(type.id),
      );
      const contract = matchingContracts?.find((item) => item.active) ?? matchingContracts?.[0];
      return {
        ...type,
        contractId: contract?.id ?? null,
        version: contract ? `v${contract.version}` : null,
        active: contract?.active ?? null,
      };
    });
  }, [standardQuery.data]);

  const entries = clientContracts
    .map<ClientContractEntry>((contract) => {
      const account = contract.accountId ? accountsById.get(contract.accountId) : undefined;
      const type = STANDARD_CONTRACT_TYPES.find(
        (item) => item.id === fromApiContractType(contract.type),
      );
      return {
        id: contract.id,
        typeLabel: type?.label ?? contract.type,
        clientName: accountName(account),
        clientDocument: account?.document?.value ?? '-',
        version: `v${contract.version}`,
        statusLabel: contract.active ? 'Ativo' : 'Inativo',
        active: contract.active,
      };
    })
    .filter((entry) => {
      const search = filters.client.trim().toLocaleLowerCase('pt-BR');
      if (!search) return true;
      return `${entry.clientName} ${entry.clientDocument}`
        .toLocaleLowerCase('pt-BR')
        .includes(search);
    });

  function findContract(id: string): Contract | undefined {
    return [...(standardQuery.data?.docs ?? []), ...clientContracts].find((item) => item.id === id);
  }

  function handleTabChange(value: string) {
    const next = new URLSearchParams(searchParams);
    const resolved = resolveTab(value);
    if (resolved === DEFAULT_TAB) next.delete('tab');
    else next.set('tab', resolved);
    setSearchParams(next, { replace: true });
  }

  async function handleSave(values: {
    type: ContractType;
    accountId?: string;
    signerContactId: string | null;
    summary: Record<string, unknown>;
    clauses: Record<string, unknown>[];
  }) {
    if (!editor) return;
    if (editor.contract) {
      await updateContract.mutateAsync({
        id: editor.contract.id,
        payload: {
          type: values.type,
          accountId: values.accountId ?? null,
          signerContactId: values.signerContactId,
          summary: values.summary,
          clauses: values.clauses,
        },
      });
      toast({ variant: 'success', title: 'Contrato atualizado com sucesso.' });
    } else {
      await createContract.mutateAsync({
        type: values.type,
        scope: editor.scope,
        accountId: editor.scope === ContractScope.CLIENT ? values.accountId : null,
        signerContactId: values.signerContactId,
        summary: values.summary,
        clauses: values.clauses,
      });
      toast({ variant: 'success', title: 'Contrato criado com sucesso.' });
    }
    setEditor(null);
  }

  async function handleToggleActive(entry: ClientContractEntry) {
    try {
      if (entry.active) await inactivateContract.mutateAsync(entry.id);
      else await activateContract.mutateAsync(entry.id);
      toast({
        variant: 'success',
        title: entry.active ? 'Contrato inativado.' : 'Contrato ativado.',
      });
    } catch (error) {
      toast({
        title: 'Erro ao alterar status',
        description: errorMessage(error),
        variant: 'destructive',
      });
    }
  }

  async function handlePublish(contractId: string | null) {
    if (!contractId) return;
    try {
      const version = await createVersion.mutateAsync(contractId);
      toast({
        variant: 'success',
        title: 'Nova versão publicada.',
        description: `Versão v${version.version} disponível no histórico.`,
      });
    } catch (error) {
      toast({
        title: 'Erro ao publicar versão',
        description: errorMessage(error),
        variant: 'destructive',
      });
    }
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Contratos</h1>
        <p className="text-muted-foreground">Gerencie contratos padrão e contratos por cliente.</p>
      </div>

      <Tabs value={currentTab} onValueChange={handleTabChange}>
        <TabsList className="max-w-full overflow-x-auto">
          <TabsTrigger value="standard">Contratos Padrões</TabsTrigger>
          <TabsTrigger value="clients">Contratos por Cliente</TabsTrigger>
        </TabsList>

        <TabsContent value="standard">
          {standardQuery.isLoading ? (
            <Skeleton className="h-72 w-full" />
          ) : standardQuery.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 py-12 text-center text-destructive">
              Não foi possível carregar os contratos padrão.
            </div>
          ) : (
            <StandardContractsTab
              contracts={standardContracts}
              onConfigure={(item) =>
                setEditor({
                  scope: ContractScope.STANDARD,
                  initialType: toApiContractType(item.id),
                })
              }
              onEdit={(item) => {
                const contract = item.contractId ? findContract(item.contractId) : undefined;
                if (contract) setEditor({ scope: ContractScope.STANDARD, contract });
              }}
              onPublishVersion={(item) => handlePublish(item.contractId)}
            />
          )}
        </TabsContent>

        <TabsContent value="clients">
          <ClientContractsTab
            entries={entries}
            isLoading={clientQuery.isLoading || accountQueries.some((query) => query.isLoading)}
            isError={clientQuery.isError}
            page={clientQuery.data?.page ?? page}
            totalPages={clientQuery.data?.pages ?? 1}
            total={clientQuery.data?.total ?? 0}
            limit={clientQuery.data?.limit ?? CLIENT_PAGE_SIZE}
            onPageChange={setPage}
            onCreateContract={() => setEditor({ scope: ContractScope.CLIENT })}
            onEdit={(entry) => {
              const contract = findContract(entry.id);
              if (contract) setEditor({ scope: ContractScope.CLIENT, contract });
            }}
            onToggleActive={handleToggleActive}
            onHistory={setHistoryTarget}
            onPublishVersion={(entry) => handlePublish(entry.id)}
            onApplyFilters={(values) => {
              setFilters(values);
              setPage(1);
            }}
            onClearFilters={() => {
              setFilters({ client: '', type: FILTER_ALL, status: FILTER_ALL });
              setPage(1);
            }}
          />
        </TabsContent>
      </Tabs>

      <ContractFormDialog
        open={editor !== null}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
        scope={editor?.scope ?? ContractScope.CLIENT}
        contract={editor?.contract}
        initialType={editor?.initialType}
        isSaving={createContract.isPending || updateContract.isPending}
        onSubmit={handleSave}
      />

      <ContractHistoryDialog
        open={historyTarget !== null}
        onOpenChange={(open) => {
          if (!open) setHistoryTarget(null);
        }}
        contractId={historyTarget?.id ?? null}
        contractLabel={
          historyTarget ? `${historyTarget.typeLabel} - ${historyTarget.clientName}` : ''
        }
      />
    </motion.div>
  );
}
